// ── Shared filter controls ─────────────────────────────────────────────────
//
// These started life inside IndustryClosingsPage and are here because a second
// analytics page needed the same three widgets. Copying them would have been
// the third time this app grew two spellings of one control — the filter labels
// on that page read slate while every other filter row read black for exactly
// that reason, and the fix was to have one definition rather than two that
// agree today.
//
// shared/ui.js's FilterSelect stays where it is and is still the right control
// for a single-choice dropdown. These are the three it does not cover: a
// multi-select, a date range, and a numeric range.
//
// THE LABEL STYLE IS THE CONTRACT. FilterSelect renders its label inline,
// uppercase, bold and letter-spaced; everything here matches it exactly, so a
// filter row built from both reads as one row.

import { useState } from "react";
import { useEscape } from "./escape";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, HAIRLINE, HAIRLINE_FIRM } from "./theme";

export const FILTER_LABEL = {
  fontSize: 11, fontWeight: 700, color: INK_MUTED,
  textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
};

export const filterInput = {
  padding: "5px 8px", fontSize: 13, borderRadius: 6,
  border: `1px solid ${HAIRLINE_FIRM}`, background: SURFACE, color: INK_PRIMARY,
};

const linkBtn = { border: "none", background: "transparent", color: B.cyan,
                  cursor: "pointer", fontSize: 12, padding: 0 };

// A <label> MAY WRAP EXACTLY ONE FORM CONTROL. Wrapping several — two date
// boxes, or three toggle buttons — makes every one of them inherit the label's
// text as its accessible name, so the "Any" button in the Posted toggle
// announced itself as "Posted Posted Unposted" and a screen reader user could
// not tell the three apart. It also breaks click-to-focus, since the browser
// cannot know which control the label meant.
//
// So the default is a GROUP: a labelled container, which is what a two-ended
// range or a button set actually is. `as="label"` is for the single-input case,
// where wrapping is correct and buys click-to-focus for free.
export const FilterField = ({ label, children, as }) => (
  as === "label" ? (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <span style={FILTER_LABEL}>{label}</span>
      {children}
    </label>
  ) : (
    <div role="group" aria-label={label}
         style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <span style={FILTER_LABEL} aria-hidden="true">{label}</span>
      {children}
    </div>
  )
);

// A menu that closes when you click anywhere else. The invisible full-screen
// backdrop is deliberate: a document-level listener has to be removed on
// unmount and races the button's own click, and getting that wrong leaves a
// menu that will not open.
const Popover = ({ open, setOpen, children, align }) => (open ? (
  <>
    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
    <div style={{ position: "absolute", zIndex: 20, top: "100%", marginTop: 4,
                  [align === "right" ? "right" : "left"]: 0,
                  background: SURFACE, border: `1px solid ${HAIRLINE_FIRM}`,
                  borderRadius: 8, padding: 8, maxHeight: 300, overflowY: "auto",
                  minWidth: 200, boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
      {children}
    </div>
  </>
) : null);

// options: ["A"] or [{ value, label, note }]. `value` is an array; empty means
// "all", which is why the button says "All types" rather than "0 selected" —
// nothing selected and everything selected are the same query, and the label
// should say the one people mean.
export const Multi = ({ label, value, setValue, options, render, allLabel, width }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  const opts = (options || []).map(o => (o && typeof o === "object")
    ? o : { value: o, label: o });
  const show = v => {
    const o = opts.find(x => String(x.value) === String(v));
    return render ? render(v, o) : (o ? o.label : v);
  };
  return (
    <FilterField label={label}>
      <div style={{ position: "relative" }}>
        <button type="button" onClick={() => setOpen(o => !o)}
                aria-label={label} aria-expanded={open}
                style={{ ...filterInput, textAlign: "left", cursor: "pointer",
                         minWidth: width || 130,
                         // An active filter gets a cyan border, so a narrowed
                         // list never looks like an empty table.
                         borderColor: value.length ? B.cyan : HAIRLINE_FIRM }}>
          {value.length === 0 ? (allLabel || "All")
            : value.length === 1 ? show(value[0])
            : `${value.length} selected`}
        </button>
        <Popover open={open} setOpen={setOpen}>
          <div style={{ display: "flex", gap: 10, marginBottom: 6,
                        borderBottom: `1px solid ${HAIRLINE}`, paddingBottom: 6 }}>
            <button type="button" style={linkBtn}
                    onClick={() => setValue([])}>{allLabel || "All"}</button>
            <button type="button" style={linkBtn}
                    onClick={() => setValue(opts.map(o => String(o.value)))}>Select all</button>
            <button type="button" style={{ ...linkBtn, marginLeft: "auto", color: INK_MUTED }}
                    onClick={() => setOpen(false)}>Done</button>
          </div>
          {opts.map(o => (
            <label key={String(o.value)}
                   style={{ display: "flex", gap: 6, alignItems: "center",
                            fontSize: 13, padding: "3px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={value.includes(String(o.value))}
                     onChange={e => setValue(e.target.checked
                       ? [...value, String(o.value)]
                       : value.filter(v => v !== String(o.value)))} />
              <span>{o.label}</span>
              {o.note != null ? <span style={{ marginLeft: "auto", color: INK_MUTED,
                                               fontSize: 11 }}>{o.note}</span> : null}
            </label>
          ))}
        </Popover>
      </div>
    </FilterField>
  );
};

// EACH BOX BOUNDS THE OTHER. `max` on the start and `min` on the end mean the
// picker itself refuses an inverted range, so "1/1/2026 to 1/1/2025" — which
// returns nothing, for a reason an empty page cannot explain — is not reachable
// by accident.
export const DateRange = ({ label, from, to, setFrom, setTo, min, max, presets }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  return (
    <FilterField label={label || "Dates"}>
      <div style={{ display: "flex", gap: 4, alignItems: "center", position: "relative" }}>
        <input type="date" value={from} min={min || undefined} max={to || max || undefined}
               onChange={e => setFrom(e.target.value)} aria-label={`${label || "Dates"}: from`}
               style={{ ...filterInput, width: 132,
                        borderColor: from ? B.cyan : HAIRLINE_FIRM }} />
        <span style={{ color: INK_MUTED }}>–</span>
        <input type="date" value={to} min={from || min || undefined} max={max || undefined}
               onChange={e => setTo(e.target.value)} aria-label={`${label || "Dates"}: to`}
               style={{ ...filterInput, width: 132,
                        borderColor: to ? B.cyan : HAIRLINE_FIRM }} />
        {presets && presets.length ? (
          <>
            <button type="button" onClick={() => setOpen(o => !o)} title="Common date ranges"
                    style={{ ...filterInput, cursor: "pointer", padding: "5px 7px" }}>▾</button>
            <Popover open={open} setOpen={setOpen} align="right">
              {presets.map(p => (
                <button key={p.label} type="button"
                        onClick={() => { const [a, b] = p.of(); setFrom(a); setTo(b); setOpen(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left",
                                 border: "none", background: "transparent", cursor: "pointer",
                                 padding: "6px 8px", fontSize: 12, color: INK_PRIMARY }}>
                  {p.label}
                </button>
              ))}
              <button type="button" onClick={() => { setFrom(""); setTo(""); setOpen(false); }}
                      style={{ display: "block", width: "100%", textAlign: "left",
                               border: "none", background: "transparent", cursor: "pointer",
                               padding: "6px 8px", fontSize: 12, color: INK_MUTED,
                               borderTop: `1px solid ${HAIRLINE}` }}>
                All dates on file
              </button>
            </Popover>
          </>
        ) : null}
      </div>
    </FilterField>
  );
};

const iso = d => d.toISOString().slice(0, 10);
export const DATE_PRESETS = [
  { label: "Last 30 days", of: () => { const t = new Date();
      const s = new Date(t); s.setUTCDate(s.getUTCDate() - 30); return [iso(s), iso(t)]; } },
  { label: "Last 90 days", of: () => { const t = new Date();
      const s = new Date(t); s.setUTCDate(s.getUTCDate() - 90); return [iso(s), iso(t)]; } },
  { label: "This month", of: () => { const t = new Date();
      return [`${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-01`,
              iso(t)]; } },
  { label: "Last month", of: () => { const t = new Date();
      const y = t.getUTCMonth() === 0 ? t.getUTCFullYear() - 1 : t.getUTCFullYear();
      const m = t.getUTCMonth() === 0 ? 12 : t.getUTCMonth();
      const end = new Date(Date.UTC(y, m, 0));
      return [`${y}-${String(m).padStart(2, "0")}-01`, iso(end)]; } },
  { label: "Year to date", of: () => { const t = new Date();
      return [`${t.getUTCFullYear()}-01-01`, iso(t)]; } },
];

// Either bound alone works — a minimum with no maximum is "everything above
// this", which is how a floor is usually meant.
export const AmountRange = ({ label, min, max, setMin, setMax, prefix }) => (
  <FilterField label={label || "Amount"}>
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <span style={{ color: INK_MUTED, fontSize: 12 }}>{prefix || "$"}</span>
      <input value={min} onChange={e => setMin(e.target.value)} placeholder="Min"
             inputMode="decimal" aria-label={`${label || "Amount"}: minimum`}
             style={{ ...filterInput, width: 86,
                      borderColor: min ? B.cyan : HAIRLINE_FIRM }} />
      <span style={{ color: INK_MUTED }}>–</span>
      <input value={max} onChange={e => setMax(e.target.value)} placeholder="Max"
             inputMode="decimal" aria-label={`${label || "Amount"}: maximum`}
             style={{ ...filterInput, width: 86,
                      borderColor: max ? B.cyan : HAIRLINE_FIRM }} />
    </div>
  </FilterField>
);

// A three-state toggle for a boolean that also has an "either" state. Buttons
// rather than a dropdown: three options, all worth seeing without opening
// anything, and the current one is visible at rest.
export const Segmented = ({ label, value, setValue, options }) => (
  <FilterField label={label}>
    <span style={{ display: "inline-flex", border: `1px solid ${HAIRLINE_FIRM}`,
                   borderRadius: 6, overflow: "hidden" }}>
      {options.map(o => (
        <button key={String(o.value)} type="button" onClick={() => setValue(o.value)}
                aria-pressed={value === o.value} aria-label={`${label}: ${o.label}`}
                style={{ border: "none", cursor: "pointer", padding: "5px 10px", fontSize: 13,
                         fontWeight: value === o.value ? 600 : 400,
                         background: value === o.value ? B.cyan : "transparent",
                         color: value === o.value ? "#fff" : INK_MUTED }}>
          {o.label}
        </button>
      ))}
    </span>
  </FilterField>
);
